/**
 * Native mobile notifications via Capacitor Local Notifications plugin.
 *
 * Used on Android/iOS to show OS-level notifications when WebSocket events arrive,
 * mirroring the browser Notification API behavior in GlobalNotificationHandler.
 */

import { isCapacitor } from '../api/config';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let LocalNotifications: any = null;
let permissionGranted = false;
let nextNotificationId = 1;


/**
 * Initialize the native notification system.
 * Dynamically imports the plugin only on Capacitor to avoid bundling for web.
 */
export async function initNativeNotifications(): Promise<boolean> {
  if (!isCapacitor) return false;

  try {
    const mod = await import('@capacitor/local-notifications');
    LocalNotifications = mod.LocalNotifications;

    const { display } = await LocalNotifications.checkPermissions();
    if (display === 'granted') {
      permissionGranted = true;
      return true;
    }

    if (display === 'denied') {
      return false;
    }

    // prompt
    const result = await LocalNotifications.requestPermissions();
    permissionGranted = result.display === 'granted';
    return permissionGranted;
  } catch {
    return false;
  }
}

/**
 * Show a native local notification on the device.
 *
 * When soundEnabled is true, sets the `sound` property to use the bundled
 * notification chime (Android: res/raw/notification, iOS: notification.wav).
 * When false, no sound is played with the notification.
 */
export async function showNativeNotification(
  title: string,
  body: string,
  tag: string,
  data?: Record<string, string>,
  soundEnabled?: boolean,
): Promise<void> {
  if (!LocalNotifications || !permissionGranted) return;

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: nextNotificationId++,
          title,
          body,
          group: tag,
          extra: data,
          // Capacitor Local Notifications: sound file name without extension.
          // Android looks in res/raw/, iOS looks in app bundle.
          // Omitting sound (undefined) uses the default OS notification sound;
          // empty string suppresses sound entirely on some platforms.
          ...(soundEnabled === false ? {} : { sound: 'notification' }),
        },
      ],
    });
  } catch {
    // Silently fail — notification is non-critical
  }
}

/**
 * Register a listener for notification taps. Returns a cleanup function.
 */
export async function onNativeNotificationTap(
  handler: (data?: Record<string, string>) => void,
): Promise<() => void> {
  if (!LocalNotifications) return () => {};

  const listener = await LocalNotifications.addListener(
    'localNotificationActionPerformed',
    (event: { notification: { extra?: Record<string, string> } }) => {
      handler(event.notification.extra);
    },
  );

  return () => listener.remove();
}

/**
 * Whether native notifications are available (running in Capacitor).
 */
export function isNativeNotificationsAvailable(): boolean {
  return isCapacitor;
}
